import { useActor } from '@xstate/react';
import type { InterpreterFrom } from 'xstate';
import { SETTINGS } from '@hotjar/webapp/constants/routes';
import { useNavigate } from 'react-router-dom';
import type { checkoutMachine } from '../../state-machine/checkout.machine';
import { ProcessingOrder } from './components/ProcessingOrder';
import { ProcessOrderError } from './components/ProcessOrderError';
import { ProcessOrderSuccess } from './components/ProcessOrderSuccess';
import { ProcessOrderChurnSuccess } from './components/ProcessOrderChurnSuccess';
import { ProcessOrderPending } from './components/ProcessOrderPending';
import * as S from './ProcessOrderContainer.styles';
import { prettifyDate } from '~/subscription-management/utils/date.utils';

interface Props {
  checkoutService: InterpreterFrom<typeof checkoutMachine>;
}

export function ProcessOrderContainer({ checkoutService }: Props): JSX.Element {
  const [state, send] = useActor(checkoutService);
  const navigate = useNavigate();

  const {
    customer: { billingAddress, currency },
    subscription,
    costs: { renewWith },
  } = state.context;

  const processingOrder = state.matches({ processOrder: 'processing' });
  const subscriptionEndDate =
    subscription?.currentPeriodEndDate !== undefined
      ? prettifyDate(subscription?.currentPeriodEndDate)
      : undefined;

  return (
    <S.Container isLoading={processingOrder} aria-live="assertive" aria-busy={processingOrder}>
      <S.Content>
        {processingOrder && <ProcessingOrder />}
        {state.matches({ processOrder: 'doneSuccess' }) && (
          <ProcessOrderSuccess subscriptionEndDate={subscriptionEndDate} context={state.context} />
        )}
        {state.matches({ processOrder: 'doneChurnSuccess' }) && (
          <ProcessOrderChurnSuccess
            emailAddress={billingAddress?.emailAddress}
            subscriptionEndDate={subscriptionEndDate}
            currency={currency}
            price={renewWith?.amountWithTax ?? 0}
          />
        )}
        {state.matches({ processOrder: 'donePending' }) && (
          <ProcessOrderPending emailAddress={billingAddress?.emailAddress} />
        )}
        {state.matches({ processOrder: 'doneError' }) && (
          <ProcessOrderError
            onGoBack={() => navigate(SETTINGS.SUBSCRIPTIONS.path)}
            retry={() => send('RETRY')}
          />
        )}
      </S.Content>
    </S.Container>
  );
}
